import React, { useState, useEffect, useRef } from 'react'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import qs from 'query-string'
import {
  BlockingLoader,
  Container,
  Css,
  StepsProgress,
  TextBody,
  ToastContext
} from '@pmi/pmi-iqos-ds-react'
import {
  canUseDOM,
  getToastMessagesFromError,
  extractGigyaErrorCode,
  listenEvent,
  loadExternalScript,
  newRelicReport,
  PinTypes,
  parseTranslations,
  resetGrecaptcha,
  checkExternalCampaign,
  analytics,
  LineInformationPage,
  localstorage,
  constants,
  cookie,
  CommonTranslations,
  setTranslations,
  GRecaptchaStyleOverride,
  PublishedComponent,
  RegistrationStepOcr,
  RegistrationStepSagawa,
  RegistrationStepUserId,
  RegistrationStepUserInfo,
  RegistrationStepHW,
  StepButtons,
  goTo,
  Routes,
  isAuthenticated,
  JWTDecode,
  Sites
} from '@pmi/pmi-iqos-shared-react'
import { DcxApiManager } from '@zyn-common/services'
import RegisterTranslations from './register.json'

const STEP_USER_ID = 'UserIdentification'
const STEP_USER_INFO = 'UserInfo'
const STEP_HW = 'HW'
const STEP_OCR = 'OCR'
const STEP_SAGAWA = 'SAGAWA'

const registrationErrorsMappings = {
  121: 'tk_requestEmailPinError121',
  112: 'tk_requestPhonePinError112',
  114: 'tk_validateEmailPinError114',
  117: 'tk_validateEmailPinError117',
  126: 'tk_validatePhonePinError126',
  129: 'tk_validatePhonePinError129',
  124: 'tk_requestEmailPinError124'
}

const StepTextContainer = ({ isActive, title }) => {
  const cssStyles = {
    ...(isActive ? { fontWeight: '700' } : {})
  }
  return (
    <TextBody
      breakWords
      size={TextBody.sizes.xs}
      alignment={TextBody.alignments.center}>
      <Css s={cssStyles}>{title}</Css>
    </TextBody>
  )
}

const RegisterComponent = ({
  blockTC,
  contactKey,
  contextProps,
  healthWarningBlock,
  line,
  lineDD,
  ocrImage,
  onBack,
  onRegisterSuccess,
  sagawaOnDeliveryImage,
  translations
}) => {
  if (translations) {
    setTranslations(translations)
  }

  // if LINE DD flow: line flag must be false
  const api = new DcxApiManager(line)
  const lineToken = canUseDOM && window.liff && window.liff.getIDToken()

  const now = new Date()

  const userInfoDefault = {
    firstname: '',
    lastname: '',
    email: '',
    country: 'JP',
    mobileNumber: '',
    password: '',
    alreadyHasDevice: '',
    aquisitionDate: now,
    gender: '',
    prefecture: '',
    postalCode: '',
    postalCodeHidden: '',
    addressOne: '',
    addressTwo: '',
    dob: '',
    year: String(now.getFullYear()),
    month: String(now.getMonth() + 1)
  }

  const defaultStepConfig = {
    main: {
      title: null
    },
    backButton: {
      visible: true,
      disabled: false,
      label: translations.tk_back,
      loading: false,
      action: () => goToPreviousStep()
    },
    nextButton: {
      visible: true,
      disabled: false,
      label: translations.tk_next,
      loading: false,
      action: () => goToNextStep()
    },
    content: {
      layout: Container.layouts.small,
      visible: true,
      render: null
    }
  }

  const getDefaultStepStates = () => {
    return [
      {
        name: STEP_USER_ID,
        completed: false
      },
      {
        name: STEP_USER_INFO,
        completed: false
      },
      {
        name: STEP_HW,
        completed: false
      },
      {
        name: STEP_OCR,
        completed: false
      },
      {
        name: STEP_SAGAWA,
        completed: false
      }
    ]
  }

  const [callredirectUrl, setCallredirectUrl] = useState(null)
  // const [skipredirectUrl, setSkipredirectUrl] = useState(null)
  const [step, setStep] = useState(0)
  const [stepStates, setStepStates] = useState([...getDefaultStepStates()])
  const [accountVerificationStep, setAccountVerificationStep] = useState({
    emailValidation: true,
    phoneValidation: true
  })
  const [lastEmailVerified, setLastEmailVerified] = useState('')
  const [lastPhoneVerified, setLastPhoneVerified] = useState('')
  const [isImageUploaded, setIsImageUploaded] = useState(false)
  const [isStepUserIdFormValid, setIsStepUserIdFormValid] = useState(false)
  const [isStepUserInfoFormValid, setIsStepUserInfoFormValid] = useState(false)
  const [isEmailRecaptchaValid, setIsEmailRecaptchaValid] = useState(false)
  const [isPhoneRecaptchaValid, setIsPhoneRecaptchaValid] = useState(false)
  const [isRecaptchaReady, seIsRecaptchaReady] = useState(false)
  const [registrationRecaptchaData, setRegistrationRecaptchaData] = useState({})
  const [isRegistrationRecaptchaValid, setIsRegistrationRecaptchaValid] =
    useState(true)
  const [recaptchaRegistrationFail, setRecaptchaRegistrationFail] =
    useState(false)
  const [emailPin, setEmailPin] = useState('')
  const [phonePin, setPhonePin] = useState('')
  const [emailPinSent, setEmailPinSent] = useState(false)
  const [phonePinSent, setPhonePinSent] = useState(false)
  const [isEntityRegistration, setIsEntityRegistration] = useState(false)
  const [entityPlatformType, setEntityPlatformType] = useState('')
  const [isIqosphere, setIsIqosphere] = useState(false)
  const [dob, setDob] = useState(format(now, 'yyyy-MM-dd')) // set default date value to prevent date/fns "format" functions to crash
  const [registrationType, setRegistrationType] = useState(0) // 0-Normal, 7-Docomo, 11-KDDI
  const [validAge, setValidAge] = useState(false)
  const [ageVerificationToken, setAgeVerificationToken] = useState(null)
  const [errorCallbacks, setErrorCallbacks] = useState({
    email: {},
    phone: {}
  })
  const [userInfo, setUserInfo] = useState(userInfoDefault)
  const [loading, setLoading] = useState(false)
  const [pageReady, setPageReady] = useState(false)
  const [formData, setFormData] = useState({
    prefectures: []
  })
  const [isFormDrafted, setIsFormDrafted] = useState(false)
  const [isNFCFlow, setIsNFCFlow] = useState(false)
  const [nfcAlreadyRead, setNfcAlreadyRead] = useState(false)
  const [alreadyHasDeviceValue, setAlreadyHasDeviceValue] = useState('')
  const [token, setToken] = useState(null)
  const [isNHAVTokenFilled, setIsNHAVTokenFilled] = useState(false)
  const [lastUsedPhonePinMethod, setLastUsedPhonePinMethod] = useState(
    PinTypes.TEXT
  )
  const [validatingEmail, setValidatingEmail] = useState(false)
  const [validatingPhone, setValidatingPhone] = useState({
    [PinTypes.TEXT]: false,
    [PinTypes.VOICE]: false
  })
  const [showSagawaStep, setShowSagawaStep] = useState(false)
  const [selectedSagawaOption, setSelectedSagawaOption] = useState(
    RegistrationStepSagawa.sagawaOptions.onDelivery
  )
  const [lastEmailPinRequested, setLastEmailPinRequested] = useState('')
  const [lastPhonePinRequested, setLastPhonePinRequested] = useState('')
  const [isEmFlow, setIsEmFlow] = useState(false)
  const [registrationParams, setRegistrationParams] = useState(null)
  const [thankYouCallredirectUrl, setThankYouCallredirectUrl] = useState('') // callback to be passed to Thank you page managed by Drupal
  const [step2IsDraft, setStep2IsDraft] = useState(false)

  const onDomainSwitchCancelFallbackRef = useRef(null)

  const popupToast = ToastContext.getContextActions().popupToast

  const handleCloseLiffPage = () => {
    canUseDOM && window.liff && window.liff.closeWindow()
  }
  const getPlatform = obj => {
    return obj?.entityPlatform === 'docomo'
      ? 'Docomo'
      : obj?.entityPlatform === 'softbank'
      ? 'Softbank'
      : 'KDDI'
  }
  useEffect(() => {
    async function fetchData() {
      const params = qs.parse(window.location.search)
      setLoading(true)
      analytics.onRegistrationLoad()
      api.setErrorTranslations(translations)
      loadExternalScript(
        'recaptchaScriptId',
        `${constants.RECAPTCHA_BASE_URL}api.js?render=${constants.RECAPTCHA_ZYN_V3_SITE_KEY}`,
        () => {
          seIsRecaptchaReady(true)
        }
      )
      const entityAccountCreationStored = localstorage.get(
        'entityAccountCreationData'
      )
      let isEntityAccountCreationFlow = false

      if (entityAccountCreationStored) {
        const entityAccountCreationData =
          entityAccountCreationStored.registrationData
        const ageToken = entityAccountCreationStored.ageToken
        setThankYouCallredirectUrl(entityAccountCreationStored.callredirectUrl)
        localstorage.remove('entityAccountCreationData')
        let platform = getPlatform(entityAccountCreationData)
        if (entityAccountCreationStored.hasError) {
          platform = getPlatform(entityAccountCreationStored)
          popupToast({
            type: ToastContext.types.error,
            subtitle: translations.tk_entityError
          })
          analytics.registrationFillError(platform)
        } else {
          if (entityAccountCreationData.adult) {
            setIsEntityRegistration(true)
            isEntityAccountCreationFlow = true
            setRegistrationType(entityAccountCreationData.registrationType)
            setEntityPlatformType(entityAccountCreationData.entityPlatform)
            const userInfoData = {
              ...userInfo,
              ...entityAccountCreationData,
              postalCodeHidden: entityAccountCreationData.postalCode
            }
            setUserInfo(userInfoData)
            setAgeVerificationToken(ageToken)
            analytics.registrationFillSuccess(platform)
          } else {
            popupToast({
              type: ToastContext.types.error,
              subtitle: translations.tk_userIsNotAdultError
            })
            analytics.registrationFillError(platform)
          }
          setDob(
            entityAccountCreationData.dob
              ? `${entityAccountCreationData.dob.substring(
                  0,
                  4
                )}-${entityAccountCreationData.dob.substring(
                  4,
                  6
                )}-${entityAccountCreationData.dob.substring(6, 8)}`
              : null
          )
          setValidAge(entityAccountCreationData.adult)
        }
      } else if (contactKey) {
        api.auth
          .getPrefilledConsumerFormData({
            params: {
              key: decodeURIComponent(contactKey)
            }
          })
          .then(data => {
            const contact = data.ContactToConsumer
            const userInfoData = {
              ...userInfo,
              ...contact,
              mobileNumber: contact.phone
            }
            setUserInfo(userInfoData)
          })
          .catch(err => {
            analyticsApiError(err)
          })
      } else if (contextProps?.userInfo) {
        setUserInfo({ ...userInfo, ...contextProps.userInfo })
      }
      await getFormData(isEntityAccountCreationFlow)
      if (!nfcAlreadyRead) {
        readNfc()
      }
      const callredirectUrlValue = params && params.callredirectUrl
      setCallredirectUrl(callredirectUrlValue)
      if (callredirectUrlValue && callredirectUrlValue.includes('em=')) {
        setAlreadyHasDeviceValue('1')
        setIsEmFlow(true)
      }
      // setSkipredirectUrl(params && params.skipredirectUrl)
      buildFinalSteps(callredirectUrlValue, isEntityAccountCreationFlow)
      if (params && params.context) {
        const isIqosphereValue = params.context.includes(Sites.iqosphere)
        setIsIqosphere(!!isIqosphereValue)
      }
      setPageReady(true)
      setLoading(false)
    }
    fetchData()
  }, [])

  useEffect(() => {
    window.addEventListener('ondomainswitchcancel', onDomainSwitchCancel)
    return () => {
      window.removeEventListener('ondomainswitchcancel', onDomainSwitchCancel)
    }
  }, [])

  useEffect(() => {
    listenNfcParamSetEvent()
    return () => {
      listenNfcParamSetEvent(true)
    }
  }, [nfcAlreadyRead])

  useEffect(() => {
    if (alreadyHasDeviceValue && (nfcAlreadyRead || isEmFlow)) {
      const userInfoData = {
        ...userInfo,
        alreadyHasDevice: alreadyHasDeviceValue
      }
      setUserInfo(userInfoData)
    }
  }, [alreadyHasDeviceValue, nfcAlreadyRead, isEmFlow])

  useEffect(() => {
    if (stepStates[step].name === STEP_OCR) {
      analytics.onBoarding('Age Verify', 'Age Verification', 'country, id type')
    }
    if (stepStates[step].name === STEP_USER_ID) {
      analytics.onBoarding('Contact Information', 'Contact')
      analytics.virtualPageView('register - contact', 'register')
    }
  }, [step])

  useEffect(() => {
    if (isNHAVTokenFilled) {
      goToNextStep()
    }
  }, [isNHAVTokenFilled])

  const onDomainSwitchCancel = () => {
    if (onDomainSwitchCancelFallbackRef?.current) {
      onDomainSwitchCancelFallbackRef.current()
    }
  }

  const jumpToStep = name => {
    const currentStepIndex = stepStates.findIndex(s => s.name === name)
    setStep(currentStepIndex)
  }

  const setStepCompletedStatus = (name, status) => {
    const stepIndex = stepStates.findIndex(s => s.name === name)
    setStepStates([
      ...stepStates.map((s, i) => {
        return i < stepIndex
          ? { ...s }
          : i === stepIndex
          ? { ...s, completed: status }
          : { ...s, completed: !status ? false : s.completed }
      })
    ])
  }

  const getFormData = async isEntityAccountCreationFlow => {
    let data
    try {
      setLoading(true)
      data = await api.auth.getAddConsumerFormData()
      setFormData(data)
      setLoading(false)
    } catch (err) {
      analyticsApiError(err)
    }
    setAccountVerificationStep({
      emailValidation:
        data && data.validateEmailPin !== undefined
          ? data.validateEmailPin
          : true,
      phoneValidation: isEntityAccountCreationFlow
        ? false
        : data && data.validatePhonePin !== undefined
        ? data.validatePhonePin
        : true
    })
  }

  const buildFinalSteps = (callredirectUrlValue, entityAccountCreationData) => {
    const showSagawaStep = !!(
      callredirectUrlValue && callredirectUrlValue.includes(Routes.checkout)
    )
    const newStepStates = []
    getDefaultStepStates().forEach((step, i) => {
      if (step.name === STEP_OCR && showSagawaStep) return
      if (step.name === STEP_SAGAWA && !showSagawaStep) return
      if (
        (step.name === STEP_OCR || step.name === STEP_SAGAWA) &&
        entityAccountCreationData
      ) {
        return
      }
      newStepStates.push({
        ...step
      })
    })
    setStepStates(newStepStates)
    setShowSagawaStep(showSagawaStep)
  }

  const readNfc = () => {
    if (window.nfcqr_ls) {
      const registrationParamsValue = window.nfcqr_ls.get()
      if (registrationParamsValue) {
        const alreadyHasDevice = registrationParamsValue ? '1' : ''
        setAlreadyHasDeviceValue(alreadyHasDevice)
        setIsNFCFlow(!!registrationParamsValue)
        setNfcAlreadyRead(true)
        setRegistrationParams(registrationParamsValue)
      }
    }
  }

  const onNfcParamSet = () => {
    if (!nfcAlreadyRead) {
      readNfc()
    }
  }

  const listenNfcParamSetEvent = (kill = false) => {
    listenEvent('onAfterNFCLocalStorageIsSet', onNfcParamSet, kill)
  }

  const submitForm = (userInfoData, isStepUserInfo) => {
    setIsFormDrafted(false)
    setErrorCallbacks({ email: {}, phone: {} })
    setUserInfo({
      ...userInfo,
      ...userInfoData
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
    if (isStepUserInfo) {
      analytics.alreadyHasDevice(userInfoData.alreadyHasDevice)
    }
  }

  const draftForm = userInfoData => {
    setUserInfo({
      ...userInfo,
      ...userInfoData
    })
    setIsFormDrafted(true)
    setStep2IsDraft(true)
  }

  const disableEntityAccountCreationFlow = () => {
    setIsEntityRegistration(false)
    setRegistrationType(0)
    setUserInfo(userInfoDefault)
    setAgeVerificationToken(null)
    setErrorCallbacks({ email: {}, phone: {} })
    setIsFormDrafted(false)
    resetPhoneVerify()
    resetPhoneVerify()
    setIsRegistrationRecaptchaValid(true)
    setRecaptchaRegistrationFail(false)
    jumpToStep(stepStates[0].name)
    setStepCompletedStatus(stepStates[0].name, false)
    getFormData(false)
    buildFinalSteps()
  }

  const handleCaptchaValidityChange = valid => {
    valid && setRecaptchaRegistrationFail(false)
    setIsRegistrationRecaptchaValid(valid)
  }

  const handleRedirectSuccess = () => {
    const params = [`name=${userInfo.firstname}`]
    isIqosphere && params.push(`context=${Sites.iqosphere}`)
    thankYouCallredirectUrl &&
      params.push(`callredirectUrl=${thankYouCallredirectUrl}`)
    goTo(Routes.registerSuccess, false, false, params, null, Sites.zyn)
  }

  const validateRegistration = async () => {
    setLoading(true)
    if (line) {
      analytics.registrationEvent(
        'Membership Registration Sucessful',
        'JP',
        line
      )
    } else {
      analytics.registrationEvent('Step - Success', 'JP', line, true)
    }
    if (onRegisterSuccess) {
      onRegisterSuccess()
    } else if (callredirectUrl) {
      onDomainSwitchCancelFallbackRef.current = handleRedirectSuccess
      goTo(callredirectUrl)
    } else if (isNFCFlow) {
      await handleNFCRegistration()
    } else {
      lineDD
        ? goTo(Routes.lineDDRegisterThankyou)
        : line
        ? goTo(Routes.lineRegisterThankyou)
        : handleRedirectSuccess()
    }
  }

  // const handleSkipRedirection = () => {
  //   skipredirectUrl &&
  //     skipredirectUrl.includes(Routes.cart) &&
  //     cookie.set(constants.COOKIE_SHOW_SKIPPED_TOAST, true, '')
  //   skipredirectUrl ? goTo(skipredirectUrl) : handleRedirectSuccess()
  // }

  const validateSagawaRegistration = async () => {
    setStepCompletedStatus(STEP_SAGAWA, true)
    await session(token)
    callredirectUrl ? goTo(callredirectUrl) : goTo(Routes.main)
  }

  const session = async authToken => {
    setLoading(true)
    try {
      await api.auth.makeLoginUsingSSOEndpoint(authToken)
      await checkExternalCampaign(authToken, localstorage, api)
      api.auth.getPosition(true)
    } catch {}
    cookie.removeSkippedOCR()
    setLoading(false)
  }

  const handleNFCRegistration = async () => {
    if (
      registrationParams &&
      registrationParams.purchaseDate &&
      registrationParams.id
    ) {
      try {
        registrationParams.purchaseDate = parseISO(
          registrationParams.purchaseDate
        )
        setLoading(true)
        await api.profile.registerDevice({
          codentifyId: registrationParams.id,
          purchaseDate: format(registrationParams.purchaseDate, 'MM/dd/yyyy'),
          parameters: registrationParams
            ? {
                source: registrationParams.s,
                type: registrationParams.t,
                platform: registrationParams.p
              }
            : null
        })
        window.dispatchEvent(new window.CustomEvent('NFCQRDeviceRegistered'))
      } catch (err) {
        window.nfcqr_ls.set({ ...registrationParams, error: err.data[0] })
        window.dispatchEvent(new window.CustomEvent('NFCQRDeviceNotRegistered'))
      } finally {
        setLoading(false)
      }
    }
  }
  const handleRegistration = async () => {
    if (isRegistrationRecaptchaValid) {
      setLoading(true)
      const $fingerPrint = document.getElementById('fingerprint')
      const $blackBox = document.getElementById('ioBlackBox')
      checkMissingIoBlackBox($blackBox)
      let data = {
        ...(!isEntityRegistration && {
          dateOfBirth: format(userInfo.dob, 'yyyyMMdd')
        }),
        emailAddress: userInfo.email,
        password: userInfo.password,
        firstName: userInfo.firstname,
        lastName: userInfo.lastname,
        telephoneNumber: userInfo.mobileNumber,
        gender: userInfo.gender,
        isDeviceOwner: userInfo.alreadyHasDevice === '1',
        blackBox: $blackBox && $blackBox.value,
        fingerPrint: $fingerPrint && $fingerPrint.value,
        latestIqosTC: formData.iqosTC,
        latestIqosphereTC: formData.iqosphereTC,
        addresses: [
          {
            country: userInfo.country,
            area: userInfo.prefecture,
            addressLine01: userInfo.addressOne || null,
            addressLine02: userInfo.addressTwo || null,
            postalCode: userInfo.postalCode || null
          }
        ],
        registrationType: registrationType,
        HWAcceptanceTimestamp: Math.round(Date.now() / 1000),
        pinEmail: emailPin,
        pinPhone: phonePin,
        action: registrationRecaptchaData.action,
        token: registrationRecaptchaData.token,
        externalRegistration: 'N_ZYN'
      }

      // Pass CartId in case of a guest token with products in the cart
      if (!isAuthenticated()) {
        const token = cookie.getToken()
        if (token) {
          const decodedToken = JWTDecode(token)
          if (decodedToken.cart_id) {
            data = { ...data, CartId: decodedToken.cart_id }
          }
        }
      }
      api.auth
        .accountRegistration(
          data,
          line ? lineToken : ageVerificationToken,
          contextProps?.registerContext
        )
        .then(async res => {
          setToken(res.authToken)
          await session(res.authToken)
          analytics.registrationEvent(
            'Click',
            'Onboarding step - HW to AV',
            line,
            true
          )
          setLoading(false)
          res &&
            res.promotionNotices &&
            localstorage.set('promotionNotices', res.promotionNotices)
          if (!line || !lineDD) {
            if (!ageVerificationToken) {
              // NHAV
              showSagawaStep && !res.sagawaEligible && replaceSagawaByOcrStep()
              goToNextStep()
            } else {
              validateRegistration()
            }
          } else {
            goToNextStep()
          }
        })
        .catch(err => {
          const data = err.data[0]
          if (line) {
            analytics.registrationEvent(
              'Membership Registration Failed',
              'JP',
              line
            )
          } else {
            analyticsApiError(err)
          }
          if (data) {
            const gigyaErrorCode = extractGigyaErrorCode(data.message)
            if (gigyaErrorCode) {
              // is gigya error
              const gigyaTranslationLabel =
                translations['tk_gigya_err_' + gigyaErrorCode]
              let toastTitle = ''
              if (gigyaTranslationLabel && gigyaTranslationLabel !== 'TBD') {
                toastTitle = translations['tk_gigya_err_' + gigyaErrorCode]
              } else {
                toastTitle = translations.tk_submissionError
              }
              popupToast({
                type: ToastContext.types.error,
                subtitle: toastTitle
              })
            } else {
              switch (data.code) {
                case '121':
                case '124':
                  errorCallbacks &&
                    errorCallbacks.email &&
                    setErrorCallbacks({
                      ...errorCallbacks,
                      email: {
                        message: translations.tk_emailAlreadyUsed,
                        value: userInfo.email
                      }
                    })
                  popupToast({
                    type: ToastContext.types.error,
                    subtitle: translations.tk_emailAlreadyUsedLink
                  })
                  resetEmailVerify()
                  jumpToStep(STEP_USER_ID)
                  setStepCompletedStatus(STEP_USER_ID, false)
                  break
                case '112':
                  popupToast({
                    type: ToastContext.types.error,
                    subtitle: translations.tk_phoneAlreadyUsedLink
                  })
                  if (isEntityRegistration) {
                    disableEntityAccountCreationFlow()
                  } else {
                    errorCallbacks &&
                      errorCallbacks.phone &&
                      setErrorCallbacks({
                        ...errorCallbacks,
                        phone: {
                          message: translations.tk_phoneAlreadyUsed,
                          value: userInfo.mobileNumber
                        }
                      })
                    resetPhoneVerify()
                    jumpToStep(STEP_USER_ID)
                    setStepCompletedStatus(STEP_USER_ID, false)
                  }
                  break
                case '119':
                  popupToast({
                    type: ToastContext.types.error,
                    subtitle: translations.tk_iovationError
                  })
                  break
                case '100':
                  errorCallbacks &&
                    errorCallbacks.email &&
                    setErrorCallbacks({
                      ...errorCallbacks,
                      email: {
                        message: translations.tk_emailAlreadyUsed,
                        value: userInfo.email
                      }
                    })
                  resetEmailVerify()
                  jumpToStep(STEP_USER_ID)
                  setStepCompletedStatus(STEP_USER_ID, false)
                  break
                case 'adr_01':
                  popupToast({
                    type: ToastContext.types.error,
                    subtitle: translations.tk_addressUnrecognizableCharacters
                  })
                  jumpToStep(STEP_USER_INFO)
                  setStepCompletedStatus(STEP_USER_INFO, false)
                  break
                default:
                  popupToast({
                    type: ToastContext.types.error,
                    subtitle: translations.tk_submissionError
                  })
              }
            }
            analytics.onBoardingError(data.message)
          } else {
            popupToast({
              type: ToastContext.types.error,
              subtitle: translations.tk_submissionError
            })
            analytics.onBoardingError(err)
          }
          if (
            data &&
            data.code &&
            (data.code === constants.CAPTCHA_VALIDATION_ERROR_01 ||
              data.code === constants.CAPTCHA_VALIDATION_ERROR_02)
          ) {
            setRecaptchaRegistrationFail(true)
          } else {
            resetGrecaptcha(constants.REGISTRATION_GRECAPTCHA_ACTION)
          }
          setLoading(false)
        })
    }
  }

  const replaceSagawaByOcrStep = () => {
    stepStates.forEach((step, index) => {
      if (step.name === STEP_SAGAWA) {
        getDefaultStepStates().forEach(s => {
          if (s.name === STEP_OCR) {
            stepStates[index] = { ...s }
          }
        })
      }
    })
  }

  const checkMissingIoBlackBox = $blackBox => {
    const missingBlackBox = !($blackBox && $blackBox.value)
    const missingJs = !(window && window.IGLOO)
    if (missingBlackBox || missingJs) {
      newRelicReport(
        new Error(
          `Missing blackbox: ${missingBlackBox}. Missing external JS: ${missingJs}`
        )
      )
    }
  }

  const requestEmailPin = async (email, recaptchaData) => {
    try {
      setValidatingEmail(true)
      await api.auth.requestEmailVerificationPINRegistration(
        email,
        recaptchaData.action,
        recaptchaData.token,
        contextProps?.pinRequestContext
      )
      errorCallbacks &&
        errorCallbacks.email &&
        setErrorCallbacks({
          ...errorCallbacks,
          email: {}
        })
      setLastEmailPinRequested(email)
    } catch (err) {
      analyticsApiError(err)
      const errorCode = err.data[0] && err.data[0].code

      if (
        errorCode === constants.CAPTCHA_VALIDATION_ERROR_01 ||
        errorCode === constants.CAPTCHA_VALIDATION_ERROR_02
      ) {
        throw err
      }

      const toastTitle = translations[registrationErrorsMappings[errorCode]]
        ? translations[registrationErrorsMappings[errorCode]]
        : getToastMessagesFromError(err, translations.tk_errorRequestEmailPin)

      popupToast({
        type: ToastContext.types.error,
        title: toastTitle
      })
      switch (errorCode) {
        case '121':
        case '124':
          errorCallbacks &&
            errorCallbacks.email &&
            setErrorCallbacks({
              ...errorCallbacks,
              email: {
                message: translations.tk_emailAlreadyUsed,
                value: email
              }
            })
          jumpToStep(STEP_USER_ID)
          setStepCompletedStatus(STEP_USER_ID, false)
          break
      }
      throw err
    } finally {
      setValidatingEmail(false)
    }
  }

  const requestPhonePin = async (phoneNumber, type, recaptchaData) => {
    const data = {
      phoneNumber,
      action: recaptchaData.action,
      token: recaptchaData.token
    }
    let requestFunc = null
    let errorLabel = null
    switch (type) {
      case PinTypes.TEXT:
        requestFunc = api.auth.requestSMSVerificationPINRegistration
        errorLabel = translations.tk_errorRequestPhonePinText
        break
      case PinTypes.VOICE:
        requestFunc = api.auth.requestVoiceVerificationPINRegistration
        errorLabel = translations.tk_errorRequestPhonePinCall
        break
      default:
        break
    }
    try {
      setLastUsedPhonePinMethod(type)
      setValidatingPhone({ ...validatingPhone, [type]: true })
      await requestFunc(data)
      errorCallbacks &&
        errorCallbacks.phone &&
        setErrorCallbacks({
          ...errorCallbacks,
          phone: {}
        })
      setLastPhonePinRequested(phoneNumber)
    } catch (err) {
      analyticsApiError(err)
      const errorCode = err.data[0] && err.data[0].code
      if (
        errorCode === constants.CAPTCHA_VALIDATION_ERROR_01 ||
        errorCode === constants.CAPTCHA_VALIDATION_ERROR_02
      ) {
        throw err
      }
      const toastTitle = translations[registrationErrorsMappings[errorCode]]
        ? translations[registrationErrorsMappings[errorCode]]
        : getToastMessagesFromError(err, errorLabel)
      popupToast({
        type: ToastContext.types.error,
        title: toastTitle
      })
      switch (errorCode) {
        case '112':
          errorCallbacks &&
            errorCallbacks.phone &&
            setErrorCallbacks({
              ...errorCallbacks,
              phone: {
                message: translations.tk_phoneAlreadyUsed,
                value: phoneNumber
              }
            })
          jumpToStep(STEP_USER_ID)
          setStepCompletedStatus(STEP_USER_ID, false)
          break
      }
      throw err
    } finally {
      setValidatingPhone({ ...validatingPhone, [type]: false })
    }
  }

  const verifyEmailPin = async (emailPin, email) => {
    setValidatingEmail(true)
    try {
      await api.auth.verifyEmailPinRegistration(email, emailPin)
      setLastEmailVerified(email)
      analytics.registrationEvent('Step - Email Verification Complete')
    } catch (err) {
      handleTwoFAPinErrors(err)
    } finally {
      setValidatingEmail(false)
    }
  }

  const verifyPhonePin = async (phonePin, phone) => {
    setValidatingPhone({ ...validatingPhone, [lastUsedPhonePinMethod]: true })
    try {
      await api.auth.verifyPhonePinRegistration(phone, phonePin)
      setLastPhoneVerified(phone)
      analytics.registrationEvent('Step - Phone Verification Complete')
    } catch (err) {
      handleTwoFAPinErrors(err)
    } finally {
      setValidatingPhone({
        ...validatingPhone,
        [lastUsedPhonePinMethod]: false
      })
    }
  }

  const handleTwoFAPinErrors = err => {
    const errorCode = err.data[0] && err.data[0].code
    const toastTitle = translations[registrationErrorsMappings[errorCode]]
      ? translations[registrationErrorsMappings[errorCode]]
      : getToastMessagesFromError(err)
    popupToast({
      type: ToastContext.types.error,
      title: toastTitle
    })
  }

  const uploadID = file => {
    setLoading(true)
    analytics.genericEvent('onboardingUploadIDFront')
    RegistrationStepOcr.uploadID({
      file,
      popupToast,
      translations,
      resultCb: idData => {
        setValidAge(idData.isAgeValid)
        idData.ageVerificationToken &&
          setAgeVerificationToken(idData.ageVerificationToken)
        setDob(idData.date)
        setIsImageUploaded(true)
        setLoading(false)
        analytics.genericEvent('onboardingUploadSuccess')
        analytics.virtualPageView(
          'register - age verification confirmation',
          'register'
        )
      },
      errorCb: () => {
        setLoading(false)
      },
      line
    })
  }

  const verifyAge = (ageToken = ageVerificationToken) => {
    setLoading(true)
    api.auth
      .verifyAge(ageToken, token, false, contextProps?.verifyAgeContext)
      .then(async res => {
        setToken(res.auth_token)
        analytics.onBoarding('OCR', 'OCR')
        if (line) {
          try {
            await api.profile.linkAccount(res.auth_token)
            setIsNHAVTokenFilled(true)
          } catch (e) {
            setLoading(false)
            popupToast({
              type: ToastContext.types.error,
              subtitle: translations.tk_errorLineAccountNotLinked
            })
          }
        } else {
          setIsNHAVTokenFilled(true)
        }
      })
      .catch(err => {
        setLoading(false)
        popupToast({
          type: ToastContext.types.error,
          subtitle: getToastMessagesFromError(
            err,
            translations.tk_registrationAgeValidationError
          )
        })
      })
  }

  const resetRegistrationForm = () => {
    setUserInfo(userInfoDefault)
    setIsImageUploaded(false)
    setErrorCallbacks({ email: {}, phone: {} })
    setIsFormDrafted(false)
    resetEmailVerify()
    resetPhoneVerify()
    setValidatingEmail(false)
    setValidatingPhone({
      [PinTypes.TEXT]: false,
      [PinTypes.VOICE]: false
    })
    readNfc()
    setStepCompletedStatus(stepStates[0].name, false)
    window.history.back()
  }

  const resetEmailVerify = () => {
    setEmailPin('')
    setEmailPinSent(false)
    setLastEmailVerified('')
  }

  const resetPhoneVerify = () => {
    setPhonePin('')
    setPhonePinSent(false)
    setLastPhoneVerified('')
  }

  const analyticsApiError = err => {
    const code = err && err.data && err.data[0] && err.data[0].code
    const message = err && err.data && err.data[0] && err.data[0].message
    analytics.registrationEvent(
      'Error',
      `${code || err.status} - ${message || err.message}`
    )
  }

  const goToPreviousStep = skips => {
    let currentStep = step
    let previousStep = currentStep - 1
    if (currentStep > 0) {
      const skipIndexes = (skips || []).map(skipName =>
        stepStates.findIndex(stepState => skipName === stepState.name)
      )
      while (currentStep > 0) {
        if (
          skipIndexes.find(skipIndex => skipIndex === previousStep) ===
          undefined
        ) {
          break
        }
        currentStep--
        previousStep--
      }
    }
    currentStep === 0 ? resetRegistrationForm() : setStep(previousStep)
  }

  const goToNextStep = skips => {
    let currentStep = step
    let nextStep = currentStep + 1
    const maxStep = stepStates.length - 1
    if (currentStep < maxStep) {
      const skipIndexes = (skips || []).map(skipName =>
        stepStates.findIndex(stepState => skipName === stepState.name)
      )
      while (currentStep < maxStep) {
        if (
          skipIndexes.find(skipIndex => skipIndex === nextStep) === undefined
        ) {
          break
        }
        currentStep++
        nextStep++
      }
    }
    setStepCompletedStatus(stepStates[currentStep].name, true)
    currentStep === maxStep ? validateRegistration() : setStep(nextStep)
  }

  const getStepOcrConfig = () => {
    const ocrConfig = { ...defaultStepConfig }
    ocrConfig.main.title = translations.tk_accountRegistrationStep
    ocrConfig.backButton.disabled = loading
    ocrConfig.backButton.action = () => {
      isImageUploaded ? setIsImageUploaded(false) : goToPreviousStep()
    }
    ocrConfig.nextButton.visible = validAge
    ocrConfig.nextButton.disabled = loading
    ocrConfig.nextButton.loading = loading
    ocrConfig.nextButton.label = translations.tk_uploadIdConfirm
    ocrConfig.nextButton.action = () => {
      analytics.registrationEvent('Step - Upload ID - Confirm')
      verifyAge()
    }
    ocrConfig.content.render = (getStepActions, buttonConfig) => {
      return (
        <RegistrationStepOcr
          translations={translations}
          loading={loading}
          dob={dob}
          validAge={validAge}
          uploadIDHandler={uploadID}
          skipHandler={false}
          isImageUploaded={isImageUploaded}
          getStepActions={getStepActions}
          actionConfig={buttonConfig}
          ocrImage={ocrImage}
        />
      )
    }
    return ocrConfig
  }

  const getStepSagawaConfig = () => {
    const sagawaConfig = { ...defaultStepConfig }
    sagawaConfig.main.title = translations.tk_accountRegistrationStep
    sagawaConfig.backButton.visible = isImageUploaded
    sagawaConfig.backButton.disabled = loading
    sagawaConfig.backButton.action = () => {
      setIsImageUploaded(false)
    }
    sagawaConfig.nextButton.visible =
      selectedSagawaOption ===
        RegistrationStepSagawa.sagawaOptions.onDelivery ||
      (selectedSagawaOption === RegistrationStepSagawa.sagawaOptions.uploadId &&
        isImageUploaded &&
        validAge)
    sagawaConfig.nextButton.disabled = loading
    sagawaConfig.nextButton.loading = loading
    sagawaConfig.nextButton.label =
      selectedSagawaOption === RegistrationStepSagawa.sagawaOptions.onDelivery
        ? translations.tk_buttonVerifyOnDelivery
        : translations.tk_uploadIdConfirm
    sagawaConfig.nextButton.action = () => {
      if (
        selectedSagawaOption === RegistrationStepSagawa.sagawaOptions.onDelivery
      ) {
        analytics.registrationEvent('Verify ID on Delivery')
        validateSagawaRegistration()
      } else if (
        selectedSagawaOption === RegistrationStepSagawa.sagawaOptions.uploadId
      ) {
        analytics.registrationEvent('Step - Upload ID - Confirm')
        verifyAge()
      }
    }
    sagawaConfig.content.render = (getStepActions, buttonConfig) => {
      return (
        <RegistrationStepSagawa
          translations={translations}
          loading={loading}
          uploadIDHandler={uploadID}
          sagawaOnDeliveryImage={sagawaOnDeliveryImage}
          selectedSagawaOption={selectedSagawaOption}
          setSelectedSagawaOption={setSelectedSagawaOption}
          getStepActions={getStepActions}
          actionConfig={buttonConfig}
          isImageUploaded={isImageUploaded}
          validAge={validAge}
          onUploadId={() => {
            analytics.registrationEvent('Verify ID by Upload')
          }}
          dob={dob}
          ocrImage={ocrImage}
        />
      )
    }
    return sagawaConfig
  }

  const getStepUserIdConfig = () => {
    const userIdConfig = { ...defaultStepConfig }
    userIdConfig.main.title = translations.tk_customerIdentificationStep
    userIdConfig.backButton.visible = !!onBack
    userIdConfig.backButton.action = () => {
      onBack && onBack()
    }
    userIdConfig.nextButton.disabled =
      !isStepUserIdFormValid ||
      loading ||
      (accountVerificationStep.emailValidation && !lastEmailVerified) ||
      (accountVerificationStep.phoneValidation && !lastPhoneVerified) ||
      validatingEmail ||
      validatingPhone[PinTypes.TEXT] ||
      validatingPhone[PinTypes.VOICE]
    userIdConfig.nextButton.loading = loading
    userIdConfig.nextButton.label = translations.tk_customerIdentificationNext
    userIdConfig.nextButton.action = data => {
      submitForm(data)
      goToNextStep()
      analytics.registrationEvent('Step - Contact Information Complete')
    }
    userIdConfig.content.visible =
      accountVerificationStep.emailValidation ||
      accountVerificationStep.phoneValidation
        ? isRecaptchaReady
        : true
    userIdConfig.content.render = (getStepActions, buttonConfig) => {
      return (
        <RegistrationStepUserId
          loading={loading}
          validatingEmail={validatingEmail}
          validatingPhone={validatingPhone}
          translations={translations}
          isEntityRegistration={isEntityRegistration}
          entityPlatformType={entityPlatformType}
          userInfo={userInfo}
          isFormDrafted={isFormDrafted}
          errorCallbacks={errorCallbacks}
          updateFormValidity={setIsStepUserIdFormValid}
          getStepActions={getStepActions}
          actionConfig={buttonConfig}
          accountVerificationStep={accountVerificationStep}
          lastEmailVerified={lastEmailVerified}
          setLastEmailVerified={setLastEmailVerified}
          requestEmailPin={requestEmailPin}
          verifyEmailPin={verifyEmailPin}
          emailPinSent={emailPinSent}
          setEmailPinSent={setEmailPinSent}
          emailPin={emailPin}
          setEmailPin={setEmailPin}
          isEmailRecaptchaValid={isEmailRecaptchaValid}
          setIsEmailRecaptchaValid={setIsEmailRecaptchaValid}
          lastPhoneVerified={lastPhoneVerified}
          setLastPhoneVerified={setLastPhoneVerified}
          requestPhonePin={requestPhonePin}
          verifyPhonePin={verifyPhonePin}
          phonePinSent={phonePinSent}
          setPhonePinSent={setPhonePinSent}
          phonePin={phonePin}
          setPhonePin={setPhonePin}
          isPhoneRecaptchaValid={isPhoneRecaptchaValid}
          setIsPhoneRecaptchaValid={setIsPhoneRecaptchaValid}
          lastUsedPhonePinMethod={lastUsedPhonePinMethod}
          lastEmailPinRequested={lastEmailPinRequested}
          lastPhonePinRequested={lastPhonePinRequested}
          showClosePageWarning={contextProps?.showClosePageWarning}
          isZyn={true}
        />
      )
    }
    return userIdConfig
  }

  const getStepUserInfoConfig = () => {
    const userInfoConfig = { ...defaultStepConfig }
    userInfoConfig.main.title = translations.tk_customerInformationStep
    userInfoConfig.backButton.disabled = loading
    userInfoConfig.backButton.label = translations.tk_customerInformationBack
    userInfoConfig.backButton.action = data => {
      draftForm(data)
      goToPreviousStep()
    }
    userInfoConfig.nextButton.disabled = !isStepUserInfoFormValid || loading
    userInfoConfig.nextButton.loading = loading
    userInfoConfig.nextButton.label = translations.tk_customerInformationNext
    userInfoConfig.nextButton.action = data => {
      submitForm(data, true)
      goToNextStep()
      setStep2IsDraft(false)
      analytics.registrationEvent('Step - Additional Information Complete')
    }
    userInfoConfig.content.render = (getStepActions, buttonConfig) => {
      return (
        <RegistrationStepUserInfo
          actionConfig={buttonConfig}
          blockTC={blockTC}
          formData={formData}
          getStepActions={getStepActions}
          isEntityRegistration={isEntityRegistration}
          entityPlatformType={entityPlatformType}
          isFormDrafted={isFormDrafted}
          loading={loading}
          translations={translations}
          updateFormValidity={setIsStepUserInfoFormValid}
          userInfo={userInfo}
          step2IsDraft={step2IsDraft}
        />
      )
    }
    return userInfoConfig
  }

  const getStepHWConfig = () => {
    const hwConfig = { ...defaultStepConfig }
    hwConfig.main.title = translations.tk_termsOfUseTitle
    hwConfig.backButton.disabled = loading
    hwConfig.backButton.action = () => {
      goToPreviousStep()
    }
    hwConfig.nextButton.disabled = loading || !isRegistrationRecaptchaValid
    hwConfig.nextButton.loading = loading
    hwConfig.nextButton.label = translations.tk_termsOfUseConfirm
    hwConfig.nextButton.action = data => {
      analytics.registrationEvent('T&C Agreement - Confirm')
      handleRegistration()
    }
    hwConfig.content.render = (getStepActions, buttonConfig) => {
      return (
        <RegistrationStepHW
          translations={translations}
          loading={loading}
          setRegistrationRecaptchaData={setRegistrationRecaptchaData}
          handleCaptchaValidityChange={handleCaptchaValidityChange}
          recaptchaRegistrationFail={recaptchaRegistrationFail}
          healthWarningBlock={healthWarningBlock}
          getStepActions={getStepActions}
          actionConfig={buttonConfig}
          isZyn={true}
        />
      )
    }
    return hwConfig
  }

  const stepConfigs = {}
  stepConfigs[STEP_USER_ID] = getStepUserIdConfig
  stepConfigs[STEP_USER_INFO] = getStepUserInfoConfig
  stepConfigs[STEP_HW] = getStepHWConfig
  stepConfigs[STEP_OCR] = getStepOcrConfig
  stepConfigs[STEP_SAGAWA] = getStepSagawaConfig

  const config = { ...stepConfigs[stepStates[step].name]() }
  const isStepActive = index => stepStates[step].name === stepStates[index].name

  if (pageReady) {
    return (
      <>
        {line && isAuthenticated() ? (
          <LineInformationPage
            translations={translations}
            handleLineGoBack={handleCloseLiffPage}
          />
        ) : (
          <>
            <GRecaptchaStyleOverride />
            <Container bg={Container.backgrounds.hardWhite}>
              <Container
                layout={Container.layouts.small}
                px={['8', '8', '16']}
                pt={['8', '8', '12']}
                pb={['8', '8', '6']}>
                <StepsProgress
                  steps={stepStates}
                  getStepText={index => {
                    return (
                      <StepTextContainer
                        title={stepConfigs[stepStates[index].name]().main.title}
                        isActive={isStepActive(index)}
                      />
                    )
                  }}
                  getStepContent={index => (
                    <TextBody darkMode={!isStepActive(index)}>
                      {index + 1}
                    </TextBody>
                  )}
                  isStepActive={index => isStepActive(index)}
                  isStepCompleted={index => stepStates[index].completed}
                  isStepTextOnMobile
                />
              </Container>
            </Container>
            <Container
              layout={Container.layouts.full}
              pt="8"
              px={['8', '8', '16']}
              pb={['8', '8', '20']}>
              <Container
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.center}>
                <Container fullWidth>
                  {config.content.visible && (
                    <Container layout={config.content.layout}>
                      {config.content.render(
                        (buttonConfig, data, stepBlocking) => {
                          return (
                            <StepButtons
                              buttonConfig={buttonConfig}
                              data={data}
                              stepBlocking={stepBlocking}
                            />
                          )
                        },
                        {
                          backButton: { ...config.backButton },
                          nextButton: { ...config.nextButton }
                        }
                      )}
                    </Container>
                  )}
                </Container>
              </Container>
            </Container>
          </>
        )}
      </>
    )
  }
  return (
    <Container
      position={Container.positions.relative}
      height="96"
      layout={Container.layouts.small}>
      <BlockingLoader loading />
    </Container>
  )
}

const Register = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={RegisterComponent}
        fullWidth
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...RegisterTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'Register',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

Register.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: () => {
      return {
        blockTC: req.body.extra && req.body.extra.terms,
        contactKey: req.query.key,
        healthWarningBlock: req.body.extra && req.body.extra.health_warning,
        line: req.query.line ? true : null, // if LINE DD flow: this flag must be false
        lineDD: req.query.lineDD ? true : null,
        ocrImage: req.body.extra && req.body.extra.ocr_icon,
        sagawaOnDeliveryImage:
          req.body.extra && req.body.extra.sagawa_on_delivery_image
      }
    }
  })
}

export default Register
